import type React from 'react'

/**
 * Roving-tabindex keyboard navigation for `role="tablist"` widgets.
 *
 * WAI-ARIA Authoring Practices require a tablist to be a single tab stop:
 * one tab has `tabIndex={0}`, the rest `tabIndex={-1}`, and Arrow / Home /
 * End move the active tab. Every tablist in the app hand-rolled the markup
 * but shipped no key handling, so a keyboard user could reach the first tab
 * and go no further.
 *
 * Usage:
 *   <div role="tablist" onKeyDown={(e) => handleTabListKeyDown(e, {
 *     activeIndex: TABS.findIndex(t => t.id === activeTab),
 *     count: TABS.length,
 *     onSelect: (i) => setActiveTab(TABS[i].id),
 *   })}>
 */

export type TabOrientation = 'horizontal' | 'vertical'

/**
 * Index the given key should move focus to, or `null` when the key is not
 * part of the tablist key set (so the caller leaves the event alone).
 * Arrow movement wraps at both ends.
 */
export function nextTabIndex(
  key: string,
  current: number,
  count: number,
  orientation: TabOrientation = 'horizontal',
): number | null {
  if (count <= 0) return null

  const forward = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
  const backward = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'

  // Clamp so a stale/unknown active index still produces sane movement.
  const from = current < 0 || current >= count ? 0 : current

  switch (key) {
    case forward:
      return (from + 1) % count
    case backward:
      return (from - 1 + count) % count
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

interface TabListKeyDownOptions {
  activeIndex: number
  count: number
  onSelect: (index: number) => void
  orientation?: TabOrientation
}

/**
 * `onKeyDown` handler for the tablist container. Selects and focuses the
 * next tab, following the automatic-activation pattern the app's tabs
 * already use for clicks (selection follows focus).
 *
 * Focus is moved by querying `[role="tab"]` inside the container, so the
 * DOM order of the tabs is the navigation order — no refs to thread.
 */
export function handleTabListKeyDown(
  event: React.KeyboardEvent<HTMLElement>,
  { activeIndex, count, onSelect, orientation = 'horizontal' }: TabListKeyDownOptions,
): void {
  const next = nextTabIndex(event.key, activeIndex, count, orientation)
  if (next === null) return

  event.preventDefault()
  event.stopPropagation()
  onSelect(next)

  const tabs = event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')
  tabs[next]?.focus()
}
